/**
 * 显示提示消息（替换 alert）
 * @param {string} message - 提示内容
 * @param {string} type - 消息类型：'success' | 'error' | 'info' | 'warning'
 * @param {number} duration - 显示时长（毫秒），默认1500
 */
export const showToast = async (message, type = 'info', duration = 1500) => {
    // 图标映射
    const iconMap = {
        'success': 'success',
        'error': 'error',
        'info': 'info',
        'warning': 'warn'
    };

    // 使用统一的环境检测逻辑
    const realFeishuEnv = isRealFeishuEnv();
    
    // 尝试获取飞书SDK对象（依赖UserContext中已完成的鉴权）
    const sdkResult = getFeishuSDK();
    
    // 调试信息
    console.log('showToast - 环境检测:', realFeishuEnv);
    console.log('showToast - SDK:', sdkResult ? sdkResult.type : 'null');

    // 尝试调用飞书原生API
    if (realFeishuEnv && sdkResult) {
        const sdk = sdkResult.sdk;
        const sdkType = sdkResult.type;
        
        if (typeof sdk.showToast === 'function') {
            console.log(`调用 ${sdkType}.showToast:`, message, 'type:', type);
            sdk.showToast({
                title: message,
                icon: iconMap[type] || 'info',
                duration: duration,
                success: () => {
                    console.log('showToast success:', message);
                },
                fail: (err) => {
                    console.error(`${sdkType}.showToast fail:`, err);
                    // 如果showToast失败，使用 alert
                    alert(message);
                }
            });
            return;
        }
        
        console.warn('真实飞书环境但SDK.showToast不可用，降级使用原生alert');
    }
    
    // 非飞书环境或SDK不可用，降级使用原生 alert
    alert(message);
};

/**
 * 判断是否为真实飞书环境
 * @returns {boolean}
 */
const isRealFeishuEnv = () => {
    // 获取当前页面URL
    const location = window.location.href.toLowerCase();
    
    // 判断逻辑：只要是 localhost:3000 就是飞书开发者工具（非真实环境）
    // 其他所有情况都算真实环境
    if (location.includes('localhost:3000')) {
        console.log('检测到 localhost:3000，判断为飞书开发者工具环境');
        return false;
    }
    
    // 其他所有情况都判断为真实飞书环境
    console.log('非 localhost:3000，判断为真实飞书环境');
    return true;
};

/**
 * 检查飞书JSAPI是否已完成鉴权
 * @returns {boolean}
 */
const isJSAPIAuthorized = () => {
    // 检查是否存在已鉴权的SDK对象
    const tt = window.h5sdk?.tt || window.tt;
    const feishu = window.feishu;
    const lark = window.lark;
    
    // 如果存在这些对象，说明JSAPI已经加载
    // 鉴权状态由UserContext中的handleJSAPIAccess负责
    return !!(tt || feishu || lark);
};

/**
 * 获取飞书SDK对象
 * @returns {object|null}
 */
const getFeishuSDK = () => {
    // 优先级：tt (h5sdk.tt) > feishu > lark
    const tt = window.h5sdk?.tt || window.tt;
    if (tt && typeof tt.showModal === 'function') {
        return { sdk: tt, type: 'tt' };
    }
    
    const feishu = window.feishu;
    if (feishu && typeof feishu.showModal === 'function') {
        return { sdk: feishu, type: 'feishu' };
    }
    
    const lark = window.lark;
    if (lark && typeof lark.showModal === 'function') {
        return { sdk: lark, type: 'lark' };
    }
    
    return null;
};



/**
 * 显示确认对话框（替换 confirm）
 * @param {string} title - 标题
 * @param {string} content - 内容
 * @returns {Promise<boolean>} - 用户是否确认
 */
export const showConfirm = (title = '提示', content = '') => {
    return new Promise(async (resolve) => {
        // 判断是否为真实飞书环境
        const realFeishuEnv = isRealFeishuEnv();
        
        // 尝试获取飞书SDK对象（依赖UserContext中已完成的鉴权）
        const sdkResult = getFeishuSDK();
        
        // 调试信息
        console.log('showConfirm - 环境检测:', realFeishuEnv);
        console.log('showConfirm - SDK:', sdkResult ? sdkResult.type : 'null');
        console.log('showConfirm - window.h5sdk:', !!window.h5sdk);

        // 尝试调用飞书原生API
        if (realFeishuEnv && sdkResult) {
            const sdk = sdkResult.sdk;
            const sdkType = sdkResult.type;
            
            if (typeof sdk.showModal === 'function') {
                console.log(`调用 ${sdkType}.showModal:`, title, content);
                sdk.showModal({
                    title: title,
                    content: content,
                    confirmText: '确定',
                    cancelText: '取消',
                    success: (res) => {
                        if (res.confirm) {
                            resolve(true);
                        } else if (res.cancel) {
                            resolve(false);
                        }
                    },
                    fail: (err) => {
                        console.error(`${sdkType}.showModal fail:`, err);
                        // 如果是invalid session错误，说明鉴权可能有问题
                        if (err.errCode === 105 || (err.errMsg && err.errMsg.includes('invalid session'))) {
                            console.log('检测到invalid session错误，JSAPI鉴权可能未完成或已过期');
                        }
                        // 降级到原生 confirm
                        const result = window.confirm(`${title}\n${content}`);
                        resolve(result);
                    }
                });
                return;
            }
            
            // 尝试使用confirm方法（某些版本的SDK可能使用此名称）
            if (typeof sdk.confirm === 'function') {
                console.log(`调用 ${sdkType}.confirm:`, title, content);
                sdk.confirm({
                    title: title,
                    content: content,
                    confirmText: '确定',
                    cancelText: '取消',
                    success: (res) => {
                        resolve(res.confirm);
                    },
                    fail: (err) => {
                        console.error(`${sdkType}.confirm fail:`, err);
                        const result = window.confirm(`${title}\n${content}`);
                        resolve(result);
                    }
                });
                return;
            }
            
            console.warn(`真实飞书环境但${sdkType}.showModal不可用，降级使用原生confirm`);
        }
        
        // 非飞书环境或SDK不可用：直接使用原生 confirm
        const result = window.confirm(`${title}\n${content}`);
        resolve(result);
    });
};